// tslint:disable:no-any
// tslint:disable:max-line-length
import Auth0 from 'auth0-js';
import { NotificationManager } from 'react-notifications';

import { ProfileApiFactory } from '../api';
import { ACCESS_TOKEN, EXPIRES_AT, ID_TOKEN, SETTINGS } from '../constants/authentication';
import history from './History';

export class AuthLogic {
    public static instance: AuthLogic;
    private auth0web: Auth0.WebAuth;
    private renewTimer: NodeJS.Timer | null = null;

    public static getInstance() {
        if (!AuthLogic.instance) {
            AuthLogic.instance = new AuthLogic();
        }

        return AuthLogic.instance;
    }
    /**
     * Renew token - use silent authentication
     */
    public async renew(redirectToApp: boolean = false) {
        const job = new Promise(resolve => {
            this.auth0web.checkSession(
                {
                    redirectUri: (window as any).__REACT_SETTINGS.silentCallbackUrl,
                    audience: (window as any).__REACT_SETTINGS.apiUrl,
                    responseType: 'token id_token',
                    scope: 'openid email profile read:order write:order'
                },
                (err, authResult) => {
                    // Renewed tokens or error
                    if (err) {
                        this.logout();
                    } else {
                        this.setSession(authResult, authResult.expiresIn, authResult.scope, false);
                        // redirect to dashboard
                        if (redirectToApp) {
                            history.replace('/app');
                        }
                    }
                }
            );
        });

        await job;
    }

    /**
     * Delete all information in local storage about session
     */
    public logout() {
        // Clear acc    ess token and ID token from local storage
        this.removeLocalStorage();
        // auth0 logout
        this.auth0web.logout({
            returnTo: (window as any).__REACT_SETTINGS.baseUrl
        });
        // navigate to the home route
        // history.replace('/');
    }

    public removeLocalStorage() {
        // Clear access token and ID token from local storage
        localStorage.removeItem(ID_TOKEN);
        localStorage.removeItem(ACCESS_TOKEN);
        localStorage.removeItem(EXPIRES_AT);
        // settings
        localStorage.removeItem(SETTINGS);
        // ukonceni timeru
        if (this.renewTimer !== null) {
            clearTimeout(this.renewTimer);
        }
    }

    /**
     * Handle authentication when user is authenticated
     *
     * @param {Object} authResult
     */
    public async handleAuthentication(
        authResult: AuthResult,
        expiresIn: number,
        scope: any
    ) {
        const job = new Promise(resolve => {
            /**
             * Parse result to prevent hijacking
             */
            this.auth0web.parseHash(authResult, (err, res) => {
                /**
                 * If not error, continue with authentication
                 */
                if (err === null) {
                    this.setSession(authResult, expiresIn, scope)
                        .then(result => {
                            if (
                                result instanceof Response &&
                                result.status != undefined &&
                                result.status >= 400
                            ) {
                                if (result.status == 404) {
                                    history.replace('/?NotRegistered');
                                } else {
                                    // redirect to homepage if status is not correct
                                    NotificationManager.error(
                                        'Error!',
                                        result.statusText
                                    );
                                    // history.replace('/');
                                }
                                resolve();
                            } else {
                                // redirect to app if correct
                                NotificationManager.info(
                                    'Success!',
                                    'Authorized'
                                );
                                history.replace('/app');
                                resolve();
                            }
                        })
                        .catch(error => {
                            // redirect to homepage whether there is a problem
                            // history.replace('/');
                            resolve();
                        });

                    /**
                     * In other case redirect to homepage
                     */
                } else {
                    // history.replace('/');
                    resolve();
                }
            });
        });

        await job;
    }

    public startRenewTimer(expiresIn: any) {
        if (this.renewTimer !== null) {
            clearInterval(this.renewTimer);
        }
        if (!isNaN(expiresIn) && parseInt(expiresIn, 0) > 0) {
            this.renewTimer = setTimeout(() => {
                this.renew();
            }, parseInt(expiresIn, 0));
        }
    }

    public restartRenewTimer() {
        this.startRenewTimer( (parseInt(this.expiresAt() as string, 10) - new Date().getTime()) );
    }

    /**
     * Check whether the user is really authenticated
     */
    public isAuthenticated() {
        /**
         * Check whether token exists
         */
        const token = this.getTokenSync();
        if (!token) {
            return false;
        }

        const tempExpiresAt = this.expiresAt();
        if (tempExpiresAt != undefined) {
            /**
             * Check it's expiration time
             */
            const expiresAt = JSON.parse(tempExpiresAt);

            if (!expiresAt) {
                return false;
            }

            return new Date().getTime() < expiresAt;
        }

        return false;
    }

    public getIdToken() {
        return localStorage.getItem(ID_TOKEN);
    }

    /**
     * Get expires at timestamp
     */
    public expiresAt() {
        return localStorage.getItem(EXPIRES_AT);
    }

    public async getToken() {
        await this.checkTokenValidity();
        return localStorage.getItem(ACCESS_TOKEN);
    }

    public getTokenSync() {
        return localStorage.getItem(ACCESS_TOKEN);
    }

    private async checkTokenValidity() {
        if (!this.isAuthenticated()) {
            await this.renew();
        }
    }

    /**
     * Check authentication against wetnotes server
     * @param {string} accessToken
     */
    private async sendAuth(accessToken: string) {
        try {
            const profile = await ProfileApiFactory({
                accessToken
            }).apiProfileGet();
            return profile;
        } catch (e) {
            const response = e as Response;
            return response;
        }
    }

    private async setSession(
        authResult: AuthResult,
        expiresIn?: number,
        scope?: any,
        silent: boolean = false
    ) {
        localStorage.setItem(ID_TOKEN, authResult.idToken);
        localStorage.setItem(ACCESS_TOKEN, authResult.accessToken);

        if (expiresIn) {
            const expiresAt = expiresIn * 1000 + new Date().getTime();
            localStorage.setItem(EXPIRES_AT, String(expiresAt));

            // zapne casovac
            if (expiresIn > 10) {
                this.startRenewTimer(expiresIn);
            }

            this.restartRenewTimer();
        }
        if (silent) {
            return;
        }

        return await this.sendAuth(authResult.accessToken);
    }

    private initialize() {
        this.auth0web = new Auth0.WebAuth({
            domain: (window as any).__REACT_SETTINGS.domain,
            clientID: (window as any).__REACT_SETTINGS.clientId,
            responseType: 'token id_token',
            scope: 'openid email profile read:order write:order'
        });
    }

    private constructor() {
        this.initialize();
    }
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/auth/AuthLogic.ts